import React, { memo, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { getStateName } from 'constants/states';
import { getIsFormNonOverridable } from 'utils/forms';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { openDialogAction } from 'store/global/actions';

import { formsTestIds } from '../forms.testIds';

import EditFormModal from './EditFormModal';
import { getStyles } from './FormEditor.styles';
import useFormEditorHeaderSlot from './hooks/useFormEditorHeaderSlot';

const defaultObject = {};

const DEFAULT_CLASSES = { label: 'justify-start' };

const FormEditorHeader = ({ header, formActions, form = defaultObject, isEditMode = false }) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const { historyEntityId } = useParams();
  const headerSlot = useFormEditorHeaderSlot();

  const dispatch = useDispatch();

  const state = useMemo(() => getStateName(form.state), [form.state]);
  const isHistorySnapshotMode = !!historyEntityId;

  const masterCompanyId = useSelector(selectMasterCompanyId);
  const isOverrideEnabled = useFeatureToggle(FeatureFlag.EnableFormLocalOverrides);
  const isGlobalForm = checkIsInternalCompany({ companyId: form.companyId });
  const isInternalCompany = checkIsInternalCompany({ companyId: masterCompanyId });
  const isGlobalNotInternalForm = isGlobalForm && !isInternalCompany;

  const isOverrideActive = isOverrideEnabled && !isInternalCompany;
  const isEditDisabled = isOverrideActive
    ? getIsFormNonOverridable({ formType: form.formType, isCustom: form.isCustom })
    : isGlobalNotInternalForm;

  const isTypographyComponent = isEditMode || isHistorySnapshotMode;

  const onOpenEditFormModal = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      if (isTypographyComponent) {
        return;
      }

      dispatch(
        openDialogAction({
          header: `Edit form in ${state}`,
          body: () => <EditFormModal initialData={form} isDisabled={isEditDisabled} />,
          withClosingConfirmation: !isEditDisabled,
        }),
      );
    },
    [isTypographyComponent, dispatch, state, isEditDisabled, form],
  );

  const HeaderWrapper = useMemo(() => {
    return isTypographyComponent ? Typography : Button;
  }, [isTypographyComponent]);

  const row = (
    <Box className="grid grid-cols-4 justify-between px-20 items-center" sx={classes.heading}>
      <HeaderWrapper
        className="truncate col-span-3 normal-case"
        classes={!isTypographyComponent ? DEFAULT_CLASSES : undefined}
        color={!isTypographyComponent ? 'inherit' : undefined}
        data-testid={formsTestIds.FORMS_FORM_EDITOR_HEADER}
        sx={classes.headingText}
        title={header?.length ? header : undefined}
        variant={!isTypographyComponent ? 'text' : undefined}
        onClick={onOpenEditFormModal}
      >
        {header}
      </HeaderWrapper>
      <div className="justify-self-end">{formActions}</div>
    </Box>
  );

  if (!headerSlot) {
    return row;
  }

  // Until the hosting page has mounted its slot there is nowhere to put the row yet.
  return headerSlot.slotElement ? createPortal(row, headerSlot.slotElement) : null;
};

FormEditorHeader.propTypes = {
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  formActions: PropTypes.node.isRequired,
  isEditMode: PropTypes.bool,
  form: PropTypes.object,
};

export default memo(FormEditorHeader);
